import "./studio.css";
import { api, ApiError, publicApiBase } from "../../api";
import { emptyBlock, errorBanner, loadingBlock, pill, tag } from "../shared/blocks";
import { createLifecycle, isAbort } from "../shared/lifecycle";
import { h, tidy, type Child } from "../shared/safe";
import { fleetQuery, parseClients, type FleetClient } from "../fleetModel";
import { savedTemplatesPanel } from "../templates";
import { mountViewer } from "../sitepreview/viewer";
import { platformLabel } from "../../platforms";
import {
  demoSrc, familyLabel, filterKits, hostingNotes, pageTypeLabels, parseDemoDetail, parseStudioKits, FAMILY_LABEL,
  type DemoDetail, type StudioKit,
} from "./studioModel";

// Template Studio: the library of complete-website kits, a navigable synthetic demo of each, and the choice of a template for a client.
//
//   #/templates                      library (and the saved page templates tab)
//   #/templates/preview/:kitId       the whole demo site, navigable, desktop/mobile, plus an all-pages overview
//   #/templates/use/:kitId           choose the client, then #/clients/:id/site/:kitId (the existing compose, build, review and publish workflow)
//
// Demo content is the kit's own SYNTHETIC site, served by the API outside any client. It is labelled everywhere it appears and is never mixed with
// a client's approved facts: choosing a template creates a draft from the client's own records, and anything missing is asked for, never filled from a demo.

const enc = encodeURIComponent;
const SYNTHETIC_NOTE = "Sample website. Every business, address, phone number and text in this demo is fictional.";

async function loadKits(signal?: AbortSignal): Promise<StudioKit[]> {
  const kits = parseStudioKits(await api<unknown>("/v1/site-kits", signal ? { signal } : {}));
  if (!kits) throw new Error("The server's reply was not in the expected format.");
  return kits;
}
const problem = (e: unknown, what: string): string => (e instanceof ApiError ? `${what} (HTTP ${e.status}).` : "Couldn't reach the server.");

const list = (items: readonly string[], cls = ""): HTMLElement => h("ul", { class: `st-list ${cls}`.trim() }, ...items.map((t) => h("li", null, t)));

// ------------------------------------------------------------------------------------------------------------------------ the library

export async function templateStudioView(root: HTMLElement): Promise<void> {
  const shell = h("div", { class: "st" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);
  let kits: StudioKit[] | null = null;
  let error: string | null = null;
  let search = "";
  let family = "all";
  let tab: "library" | "saved" = "library";

  const tabsEl = h("div", { class: "st-tabs", role: "tablist", "aria-label": "Template Studio" });
  const panel = h("div", { class: "st-panel", role: "tabpanel", id: "st-panel" });
  const header = h("div", { class: "topbar" }, h("div", null, h("h1", null, "Template Studio"), h("p", { class: "muted st-sub" }, "Complete websites, ready to make your own. Preview one, then apply it to a client.")));

  function drawTabs() {
    tabsEl.replaceChildren(...([["library", "Template library"], ["saved", "Saved templates"]] as const).map(([id, label]) => {
      const b = h("button", { type: "button", role: "tab", class: `st-tab${tab === id ? " on" : ""}`, "aria-selected": String(tab === id), tabindex: tab === id ? "0" : "-1", id: `st-tab-${id}`, "aria-controls": "st-panel", dataset: { tab: id } }, label) as HTMLButtonElement;
      b.addEventListener("click", () => { tab = id; drawTabs(); drawPanel(); });
      b.addEventListener("keydown", (ev) => { if (ev.key === "ArrowRight" || ev.key === "ArrowLeft") { ev.preventDefault(); tab = id === "library" ? "saved" : "library"; drawTabs(); drawPanel(); tabsEl.querySelector<HTMLButtonElement>(`#st-tab-${tab}`)?.focus(); } });
      return b;
    }));
  }

  function drawPanel() {
    if (tab === "saved") { panel.setAttribute("aria-labelledby", "st-tab-saved"); panel.replaceChildren(h("p", { class: "muted" }, "Page templates saved in this app (read-only)."), h("div", { id: "st-saved" })); void savedTemplatesPanel(panel.querySelector<HTMLElement>("#st-saved")!); return; }
    panel.setAttribute("aria-labelledby", "st-tab-library");
    if (error) return void panel.replaceChildren(errorBanner(error, { retry: () => void init() }));
    if (kits === null) return void panel.replaceChildren(loadingBlock("Loading templates", 3));
    const searchInput = h("input", { type: "search", id: "st-search", placeholder: "Search website templates", "aria-label": "Search website templates", value: search, autocomplete: "off", on: { input: (e: Event) => { search = (e.target as HTMLInputElement).value; drawGrid(); } } }) as HTMLInputElement;
    const fam = h("select", { id: "st-family", "aria-label": "Website type", on: { change: (e: Event) => { family = (e.target as HTMLSelectElement).value; drawGrid(); } } },
      h("option", { value: "all" }, "All website types"), ...[...new Set(kits.map((k) => k.family))].map((f) => h("option", { value: f, selected: f === family }, familyLabel(f)))) as HTMLSelectElement;
    const count = h("span", { class: "muted", id: "st-count", "aria-live": "polite" });
    const grid = h("div", { class: "st-grid", id: "st-grid" });
    const drawGrid = () => {
      const shown = filterKits(kits!, search, family);
      count.textContent = `${shown.length} of ${kits!.length} website kits. Every kit includes all of its pages.`;
      grid.replaceChildren(...(shown.length ? shown.map(card) : [emptyBlock("No template matches", "Clear the search or choose All website types.", [h("button", { type: "button", class: "secondary", on: { click: () => { search = ""; family = "all"; drawPanel(); } } }, "Clear filters")])]));
    };
    panel.replaceChildren(h("div", { class: "st-toolbar" }, searchInput, fam, count), grid);
    drawGrid();
    fitThumbs(panel);
  }

  function card(k: StudioKit): HTMLElement {
    const host = hostingNotes(k);
    const base = publicApiBase();
    const thumbSrc = k.demo ? demoSrc(base, k.demo.sitePath) : null;
    const thumb = h("div", { class: "st-thumb", "aria-hidden": "true" },
      thumbSrc ? homepageThumb(k.name, thumbSrc) : h("div", { class: "st-thumb__none" }, "No demo available"));
    return h("article", { class: "st-card", dataset: { kit: k.kitId, family: k.family }, "aria-labelledby": `st-h-${k.kitId}` },
      thumb,
      h("div", { class: "st-card__body" },
        h("div", { class: "st-card__top" }, h("h2", { id: `st-h-${k.kitId}` }, k.name), tag(familyLabel(k.family))),
        h("p", { class: "st-summary" }, k.summary),
        h("p", { class: "muted st-variant" }, h("strong", null, "Style: "), k.look || k.bestFor || "Complete-site layout"),
        h("p", { class: "muted st-pages" }, h("strong", null, k.demo ? `${k.demo.pageCount} pages in the demo · ` : ""), pageTypeLabels(k).join(" · ")),
        h("details", { class: "st-needs" }, h("summary", null, "What it needs"),
          h("p", { class: "muted" }, "From the client's approved records:"), list(k.needs.length ? k.needs : ["(see the preview)"]),
          k.supplied ? h("p", { class: "st-supplied" }, h("strong", null, k.supplied.required ? "You also supply: " : "Optionally you supply: "), k.supplied.description) : null),
        hostingDetails(k),
        h("div", { class: "btn-group st-actions" },
          h("a", { class: "btn", href: `#/templates/preview/${enc(k.kitId)}`, dataset: { action: "preview" }, "aria-label": `Preview site: ${k.name}` }, "Preview site"),
          k.composable ? h("a", { class: "btn secondary", href: `#/templates/use/${enc(k.kitId)}`, dataset: { action: "use" }, "aria-label": `Use template: ${k.name}` }, "Use template") : h("span", { class: "muted" }, "Not composable yet")),
      ));
  }

  async function init() {
    error = null; kits = null; drawPanel();
    const req = life.begin();
    try { const r = await loadKits(req.signal); if (!req.current()) return; kits = r; } catch (e) { if (!req.current() || isAbort(e)) return; error = problem(e, "The templates could not be loaded"); }
    drawPanel();
  }

  shell.append(header, tabsEl, panel);
  drawTabs();
  await init();
}

function hostingDetails(k: StudioKit): HTMLElement {
  const summary = !k.hosting.length ? "Publishing details" : k.hosting.some((item) => !item.fits) ? "Check provider file limits" : "Publishing file limits";
  return h("details", { class: "st-host-details", dataset: { hosting: "true" } },
    h("summary", null, summary),
    k.hosting.length ? h("ul", { class: "st-list" }, ...k.hosting.map((item) => h("li", null,
      `${platformLabel(item.platform)}: ${item.fits ? "fits CloudPush’s current publisher cap" : "over CloudPush’s current publisher cap"} · CloudPush cap ${item.maxFiles} files${item.minimumFiles ? `; this kit needs at least ${item.minimumFiles}` : ""}`,
    ))) : h("p", { class: "muted" }, "Provider limits are shown when you choose a destination."),
  );
}

function hostSummary(k: StudioKit): HTMLElement {
  if (!k.hosting.length) return h("p", { class: "muted" }, "Provider limits are checked when you choose a destination.");
  const over = k.hosting.filter((x) => !x.fits);
  return h("p", { class: "muted" }, over.length
    ? `This demo is over the file limit for ${over.map((x) => platformLabel(x.platform)).join(", ")}. Other listed destinations fit.`
    : "This demo is within the current file limits of the available destinations.");
}

/** Shows each real demo homepage, scaled from its native 1280px layout through the footer. */
function homepageThumb(name: string, src: string): HTMLIFrameElement {
  return h("iframe", { src, title: `${name} sample home page`, sandbox: "allow-scripts", tabindex: "-1", loading: "lazy", referrerpolicy: "no-referrer" }) as HTMLIFrameElement;
}

const thumbScopes = new Set<HTMLElement>();
let thumbMessageInstalled = false;

/** Scales every full-page demo thumbnail to its card width. A ResizeObserver keeps it right; without one it is set once. */
function fitThumbs(scope: HTMLElement) {
  const apply = () => scope.querySelectorAll<HTMLElement>(".st-thumb").forEach((t) => { const w = t.clientWidth || 300; t.style.setProperty("--st-scale", String(w / 1280)); });
  thumbScopes.add(scope);
  if (!thumbMessageInstalled) {
    thumbMessageInstalled = true;
    window.addEventListener("message", (event: MessageEvent) => {
      const data = event.data as { type?: unknown; height?: unknown } | null;
      if (!data || data.type !== "cloudpush-demo-size" || typeof data.height !== "number" || !Number.isFinite(data.height) || data.height < 200 || data.height > 20000) return;
      for (const owner of [...thumbScopes]) {
        if (!owner.isConnected) { thumbScopes.delete(owner); continue; }
        for (const frame of owner.querySelectorAll<HTMLIFrameElement>(".st-thumb iframe")) {
          if (frame.contentWindow !== event.source) continue;
          const thumb = frame.closest<HTMLElement>(".st-thumb");
          if (thumb) thumb.style.setProperty("--st-page-height", `${Math.ceil(data.height) + 40}px`);
        }
      }
    });
  }
  apply();
  if (typeof ResizeObserver === "function") { const ro = new ResizeObserver(apply); scope.querySelectorAll(".st-thumb").forEach((t) => ro.observe(t)); }
}

// ------------------------------------------------------------------------------------------------------------------------ the demo viewer

export async function templatePreviewView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const shell = h("div", { class: "st st-view" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);
  const kitId = params.kitId ?? "";
  shell.append(loadingBlock("Loading the demo", 4));
  const req = life.begin();
  let kit: StudioKit | undefined;
  let demo: DemoDetail | null = null;
  try {
    const kits = await loadKits(req.signal);
    kit = kits.find((k) => k.kitId === kitId);
    if (kit?.demo) {
      demo = parseDemoDetail(await api<unknown>(`/v1/site-kits/${enc(kitId)}/demo`, { signal: req.signal }));
    }
  } catch (e) {
    if (!req.current() || isAbort(e)) return;
    return void shell.replaceChildren(h("a", { href: "#/templates" }, "← Template library"), errorBanner(problem(e, "The demo could not be loaded")));
  }
  if (!req.current()) return;
  const back = h("a", { class: "btn secondary", href: "#/templates", id: "st-back" }, "← Template library");
  if (!kit || !demo) return void shell.replaceChildren(back, emptyBlock("No demo for this template", kit ? "This kit has no sample website." : "That template does not exist. Go back to the library."));
  const base = publicApiBase();
  const siteBase = demoSrc(base, demo.sitePath)!;
  const viewerHost = h("div", { class: "st-main" });
  mountViewer(viewerHost, {
    kind: "demo", idPrefix: "st", name: `${kit.name} demo`, siteSrc: siteBase, liveLabel: "Live demo",
    pages: demo.pages.flatMap((p) => { const src = demoSrc(base, p.url); return src ? [{ id: p.pageId, title: p.title, group: p.group, src }] : []; }),
    notice: SYNTHETIC_NOTE,
  });

  const side = h("aside", { class: "st-side", "aria-label": "About this template" },
    h("h2", null, "A complete website"),
    h("p", { class: "muted" }, `${demo.pageCount} coordinated pages, one consistent design.`),
    h("h3", null, "Make it yours"),
    h("p", { class: "muted" }, "Built from the client's approved records:"), list(kit.composesFrom.length ? kit.composesFrom : kit.needs),
    kit.supplied ? h("p", { class: "st-supplied" }, h("strong", null, kit.supplied.required ? "You also supply: " : "Optionally you supply: "), kit.supplied.description) : null,
    h("h3", null, "Hosting fit"),
    h("p", { class: "muted" }, `The smallest build is ${kit.hosting[0]?.minimumFiles ?? demo.pageCount + 3} files, including its pages and shared site files; each pinned image adds a file.`),
    hostSummary(kit), hostingDetails(kit),
    h("details", null, h("summary", null, "Limits you will meet"), list(kit.limitations)),
    h("details", null, h("summary", null, "What it supports"), list(kit.supports)),
    kit.composable ? h("a", { class: "btn st-use", href: `#/templates/use/${enc(kit.kitId)}`, id: "st-use" }, "Use template") : h("p", { class: "muted" }, "This kit cannot be composed yet."),
    h("p", { class: "muted" }, "Creates a draft from the client's own approved records. Nothing publishes yet."),
    h("a", { href: siteBase, target: "_blank", rel: "noopener noreferrer", id: "st-open" }, "Open demo in new tab ↗"),
  );

  shell.replaceChildren(
    h("div", { class: "st-viewbar" }, back, h("div", { class: "st-viewtitle" }, h("h1", null, kit.name), h("span", { class: "pill neutral" }, "Sample data"))),
    h("div", { class: "st-viewgrid" }, viewerHost, side),
  );
}

// ------------------------------------------------------------------------------------------------------------------------ use a template

export async function templateUseView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const shell = h("div", { class: "st st-use-view" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);
  const kitId = params.kitId ?? "";
  shell.append(loadingBlock("Loading", 3));
  const kreq = life.begin();
  let kit: StudioKit | undefined;
  try { kit = (await loadKits(kreq.signal)).find((k) => k.kitId === kitId); } catch (e) { if (!kreq.current() || isAbort(e)) return; return void shell.replaceChildren(errorBanner(problem(e, "The template could not be loaded"))); }
  if (!kreq.current()) return;
  const back = h("a", { class: "btn secondary", href: "#/templates" }, "← Template library");
  if (!kit) return void shell.replaceChildren(back, emptyBlock("That template does not exist", "Go back to the library and choose one."));
  if (!kit.composable) return void shell.replaceChildren(back, emptyBlock(`${kit.name} cannot be composed yet`, "Choose another template."));

  let search = "";
  let clients: FleetClient[] | null = null;
  let err: string | null = null;
  const results = h("div", { class: "st-clients", id: "st-clients", "aria-live": "polite" });
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clientRow = (c: FleetClient): HTMLElement => c.id
    ? h("a", { class: "st-client", href: `#/clients/${enc(c.id)}/site/${enc(kit!.kitId)}`, dataset: { clientId: c.id } }, h("strong", null, c.name), h("span", { class: "muted" }, [c.city, c.state].filter(Boolean).join(", ") || c.slug), h("span", { class: "st-client__go" }, "Create a draft →"))
    : h("div", { class: "st-client" }, h("strong", null, c.name), h("span", { class: "muted" }, "cannot be opened"));
  const draw = () => {
    if (err) return void results.replaceChildren(errorBanner(err, { retry: () => void load() }));
    if (clients === null) return void results.replaceChildren(loadingBlock("Loading clients", 3));
    if (clients.length === 0) return void results.replaceChildren(emptyBlock(search ? "No client matches that search" : "No clients yet", search ? "Try a different name." : "Add a client first.", [h("a", { class: "btn secondary", href: "#/clients/new" }, "Add a client")]));
    results.replaceChildren(...clients.map(clientRow));
  };
  async function load() {
    const req = life.begin();
    err = null; clients = null; draw();
    try {
      const body = await api<unknown>(`/v1/clients${fleetQuery(search, "all", 0, 50)}`, { signal: req.signal });
      if (!req.current()) return;
      const parsed = parseClients(body);
      if (!parsed) throw new Error("bad format");
      clients = parsed.clients;
    } catch (e) { if (!req.current() || isAbort(e)) return; err = problem(e, "The clients could not be loaded"); }
    draw();
  }
  const input = h("input", { type: "search", id: "st-client-search", placeholder: "Search clients by name", "aria-label": "Search clients", autocomplete: "off", on: { input: (e: Event) => { search = (e.target as HTMLInputElement).value; if (timer) clearTimeout(timer); timer = setTimeout(() => void load(), 250); } } });
  shell.replaceChildren(
    back,
    h("div", { class: "topbar" }, h("div", null, h("h1", null, `Use ${kit.name}`), h("p", { class: "muted st-sub" }, "Choose the client. The draft is built from that client's own approved records and approved details; nothing publishes at this step."))),
    h("div", { class: "card" },
      h("h2", null, "1. Choose a client"), input, results,
      h("h2", { class: "st-step" }, "2. What happens next"),
      list(["The client's Site screen opens with this template already chosen.", "You confirm the destination, compose from approved facts, content and images, then build and preview.", "Details the client lacks are named, never invented. Publishing is a separate, explicit step."]),
      kit.supplied ? h("p", { class: "st-supplied" }, h("strong", null, kit.supplied.required ? "This template also needs entries you supply when composing: " : "Optionally, when composing, you can supply: "), kit.supplied.description) : null),
  );
  await load();
}

export { FAMILY_LABEL };
type _Unused = Child | typeof tidy;
